import { X, FileText, Music, Play, Loader2, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatBytes, formatDuration } from "@/lib/media";
import type { AttachedMedia, UploadTask } from "@/lib/use-media-uploads";
import {
  PHOTO_MAX_RATIO, PHOTO_MIN_RATIO, VIDEO_MAX_RATIO, VIDEO_MIN_RATIO,
  clampRatio, useNaturalRatio,
} from "@/lib/media-ratio";

/**
 * Composer preview. It uses the same composition rules as the feed, so what a
 * person arranges here is what everyone else will see. Photos can be reordered
 * and removed before publishing; the first item leads the post.
 */
export function MediaAttachments({
  items, tasks, onRemove, onMove, className,
}: {
  items: AttachedMedia[];
  tasks: UploadTask[];
  onRemove: (key: string) => void;
  onMove?: (key: string, dir: -1 | 1) => void;
  className?: string;
}) {
  if (items.length === 0 && tasks.length === 0) return null;

  const visuals = items.filter((m) => m.kind === "image" || m.kind === "video");
  const files = items.filter((m) => m.kind === "audio" || m.kind === "document");
  const leadVideo = visuals.findIndex((m) => m.kind === "video");
  const hasVideo = leadVideo >= 0;

  let composition: React.ReactNode = null;
  if (visuals.length === 1) {
    composition = <Slot media={visuals[0]!} items={items} onRemove={onRemove} onMove={onMove} solo />;
  } else if (hasVideo) {
    const lead = visuals[leadVideo]!;
    const rest = visuals.filter((_, i) => i !== leadVideo);
    composition = (
      <div className="space-y-1.5">
        <Slot media={lead} items={items} onRemove={onRemove} onMove={onMove} solo />
        <div className="grid grid-cols-3 gap-1.5">
          {rest.map((m) => (
            <Slot key={m.key} media={m} items={items} onRemove={onRemove} onMove={onMove} ratio="1 / 1" />
          ))}
        </div>
      </div>
    );
  } else if (visuals.length === 3) {
    composition = (
      <div className="grid aspect-[16/11] grid-cols-3 grid-rows-2 gap-1.5">
        <Slot media={visuals[0]!} items={items} onRemove={onRemove} onMove={onMove} fill className="col-span-2 row-span-2" />
        <Slot media={visuals[1]!} items={items} onRemove={onRemove} onMove={onMove} fill />
        <Slot media={visuals[2]!} items={items} onRemove={onRemove} onMove={onMove} fill />
      </div>
    );
  } else if (visuals.length === 2) {
    composition = (
      <div className="grid grid-cols-2 gap-1.5">
        {visuals.map((m) => (
          <Slot key={m.key} media={m} items={items} onRemove={onRemove} onMove={onMove} ratio="4 / 5" />
        ))}
      </div>
    );
  } else if (visuals.length > 0) {
    composition = (
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {visuals.map((m) => (
          <Slot key={m.key} media={m} items={items} onRemove={onRemove} onMove={onMove} ratio="1 / 1" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {composition}

      {visuals.length > 1 && onMove && (
        <p className="text-xs text-muted-foreground">
          The first item leads the post. Use the arrows on a tile to change the order.
        </p>
      )}

      {files.map((m) => (
        <div key={m.key} className="relative flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5">
          {m.kind === "audio" ? (
            <>
              <Music className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <audio src={m.previewUrl} controls className="h-9 w-full min-w-0" />
            </>
          ) : (
            <>
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate text-sm">{m.name}</span>
              <span className="ml-auto shrink-0 pr-8 text-xs text-muted-foreground">{formatBytes(m.byteSize)}</span>
            </>
          )}
          <RemoveButton onClick={() => onRemove(m.key)} name={m.name} />
        </div>
      ))}

      {tasks.map((t) => (
        <div key={t.key} className="rounded-lg border border-border bg-card px-3 py-2">
          <div className="flex items-center gap-2 text-xs">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
            <span className="truncate font-medium">{t.name}</span>
            <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">{t.percent}%</span>
            <button type="button" onClick={t.cancel} className="min-h-11 shrink-0 px-1 text-muted-foreground underline-offset-2 hover:underline">
              Cancel
            </button>
          </div>
          <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${t.percent}%` }} />
          </div>
        </div>
      ))}

      {items.some((m) => m.kind === "video") && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Video goes to moderation review before anyone else can watch it. You will still see it on your own post while
          it waits.
        </p>
      )}
    </div>
  );
}

function Slot({
  media, items, onRemove, onMove, solo, ratio, fill, className,
}: {
  media: AttachedMedia;
  items: AttachedMedia[];
  onRemove: (key: string) => void;
  onMove?: (key: string, dir: -1 | 1) => void;
  solo?: boolean;
  ratio?: string;
  fill?: boolean;
  className?: string;
}) {
  const isVideo = media.kind === "video";
  const natural = useNaturalRatio(isVideo ? media.posterUrl ?? null : media.url);
  const soloRatio = isVideo
    ? clampRatio(natural ?? 16 / 9, VIDEO_MIN_RATIO, VIDEO_MAX_RATIO)
    : clampRatio(natural ?? 4 / 5, PHOTO_MIN_RATIO, PHOTO_MAX_RATIO);

  const idx = items.findIndex((m) => m.key === media.key);
  const canBack = idx > 0;
  const canForward = idx >= 0 && idx < items.length - 1;

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-lg border border-border bg-muted",
        solo && "max-h-[60svh] w-full",
        fill && "h-full w-full",
        className,
      )}
      style={solo ? { aspectRatio: String(soloRatio) } : ratio ? { aspectRatio: ratio } : undefined}
    >
      {isVideo ? (
        <div className="relative h-full w-full bg-black">
          <video
            src={media.previewUrl}
            poster={media.posterUrl ?? undefined}
            controls={solo}
            playsInline
            preload="metadata"
            className={cn("h-full w-full", solo ? "object-contain" : "object-cover")}
          />
          {!solo && (
            <span className="pointer-events-none absolute inset-0 grid place-items-center">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-black/65 text-white">
                <Play className="h-4 w-4 fill-current" />
              </span>
            </span>
          )}
          {media.durationSeconds ? (
            <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white">
              {formatDuration(media.durationSeconds)}
            </span>
          ) : null}
        </div>
      ) : (
        <img src={media.url} alt={media.name} className="h-full w-full object-cover" />
      )}

      {onMove && items.length > 1 && (
        <div className="absolute bottom-1.5 right-1.5 flex gap-1">
          <IconButton label="Move earlier" disabled={!canBack} onClick={() => onMove(media.key, -1)}>
            <ArrowLeft className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton label="Move later" disabled={!canForward} onClick={() => onMove(media.key, 1)}>
            <ArrowRight className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      )}
      <RemoveButton onClick={() => onRemove(media.key)} name={media.name} floating />
    </div>
  );
}

function IconButton({
  label, onClick, disabled, children,
}: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-8 w-8 place-items-center rounded-full bg-background/90 text-foreground shadow-sm transition-colors hover:bg-background disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function RemoveButton({ onClick, name, floating }: { onClick: () => void; name: string; floating?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Remove ${name}`}
      className={cn(
        "grid h-8 w-8 place-items-center rounded-full bg-background/90 text-foreground shadow-sm transition-colors hover:bg-background",
        floating ? "absolute right-1.5 top-1.5" : "absolute right-2 top-1/2 -translate-y-1/2",
      )}
    >
      <X className="h-4 w-4" />
    </button>
  );
}
